import EmailApp from '@/components/Email/EmailApp';
import EmailComposer from '@/components/Email/EmailComposer';
import {
  INACTIVE_SERVICE_REQUEST_STATUSES,
  processCalendarEvents,
  processServiceRequestsForCalendar,
} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import {
  LEAD_DETAILS_FOOTER_PROPERTIES,
  LEAD_PROPERTIES,
} from '@/components/Kanban/VisibilitySettings/constants';
import ArchiveAction from '@/components/ProjectDetails/ArchiveAction';
import ActivitiesCard from '@/components/ProjectDetails/Cards/ActivitiesCard';
import AppointmentsCard from '@/components/ProjectDetails/Cards/AppointmentsCard';
import CommentsCard from '@/components/ProjectDetails/Cards/CommentsCard';
import CustomerHeaderCard from '@/components/ProjectDetails/Cards/CustomerHeaderCard';
import CustomerInfoCard from '@/components/ProjectDetails/Cards/CustomerInfoCard';
import { getMergedLeadFiles } from '@/components/ProjectDetails/Cards/files';
import FilesCard from '@/components/ProjectDetails/Cards/FilesCard';
import LeadDetailsDrawerProperties from '@/components/ProjectDetails/Cards/LeadDetailsDrawerProperties';
import MailCard from '@/components/ProjectDetails/Cards/MailCard';
import MessagesCard from '@/components/ProjectDetails/Cards/MessagesCard';
import MetadataRow from '@/components/ProjectDetails/Cards/MetadataRow';
import NextActionCard from '@/components/ProjectDetails/Cards/NextActionCard';
import ProjectInfoCard from '@/components/ProjectDetails/Cards/ProjectInfoCard';
import SalesInfluencersCard from '@/components/ProjectDetails/Cards/SalesInfluencersCard';
import TasksCard from '@/components/ProjectDetails/Cards/TasksCard';
import ActivitiesDrawer from '@/components/ProjectDetails/Drawers/ActivitiesDrawer';
import AppointmentsDrawer from '@/components/ProjectDetails/Drawers/AppointmentsDrawer';
import CommentsDrawer from '@/components/ProjectDetails/Drawers/CommentsDrawer';
import DrawerLayout from '@/components/ProjectDetails/Drawers/DrawerLayout';
import FilesDrawer from '@/components/ProjectDetails/Drawers/FilesDrawer';
import MessagesDrawer from '@/components/ProjectDetails/Drawers/MessagesDrawer';
import TasksDrawer from '@/components/ProjectDetails/Drawers/TasksDrawer';
import styles from '@/components/ProjectDetails/index.less';
import { useDrawerController } from '@/components/ProjectDetails/useDrawerController';
import { useProjectDetails } from '@/components/ProjectDetails/useProjectDetails';
import { usePubSub } from '@/context/PubSubContext';
import { useHasCaratAccess, useHasSalesInfluencersAccess } from '@/utils/hooks';
import {
  APPOINTMENT_CHANGED,
  UPDATE_LEAD_IF_DRAWER_OPEN,
} from '@/context/PubSubContext/events';
import { F } from '@/utils/intl';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Skeleton, Space } from 'antd';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useIntl } from 'umi';
import CaratPlanningsCard from '@/components/ProjectDetails/Cards/CaratPlanningsCard';
import CaratPlanningsDrawer from '@/components/ProjectDetails/Drawers/CaratPlanningsDrawer';
import { getCustomerLabel } from '@/utils/contacts';

interface ProjectDetailsProps {
  projectId: string;
  onDelete?: () => void;
  highlightedAppointmentId?: string;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  projectId,
  onDelete,
  highlightedAppointmentId,
}) => {
  const dispatch = useDispatch();
  const { leadData, activities, comments, messages, loading, refetch } =
    useProjectDetails(projectId);
  // Bumped after a send so the card reflects the new thread without a reload.
  const [mailRefreshToken, setMailRefreshToken] = useState(0);
  const { activeDrawerType, activeDrawerPayload, openDrawer, closeDrawer } =
    useDrawerController();
  const { subscribe } = usePubSub();
  const hasSalesInfluencersAccess = useHasSalesInfluencersAccess();
  const hasCaratAccess = useHasCaratAccess();

  const intl = useIntl();
  useEffect(() => {
    refetch();
  }, [projectId]);

  useEffect(() => {
    const refetchIfSameLead = (payload: { leadId?: string }) => {
      if (payload?.leadId === projectId) {
        refetch();
      }
    };

    const unsubscribers = [
      subscribe(UPDATE_LEAD_IF_DRAWER_OPEN, refetchIfSameLead),
      // Deleted/cancelled from somewhere else — its own appointment details drawer,
      // the calendar — so the appointment list in here is stale.
      subscribe(APPOINTMENT_CHANGED, refetchIfSameLead),
    ];

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [projectId]);

  // The project's primary contact is who the header reaches; projects from a
  // backend without contacts fall back to the customer.
  const reachable = leadData?.primary_contact || leadData?.customer;

  // Action handlers for customer header
  const handleEmail = () => {
    if (reachable?.email) {
      window.location.href = `mailto:${reachable.email}`;
    }
  };

  const handleCall = () => {
    if (reachable?.phone) {
      window.open(`tel:${reachable.phone}`);
    }
  };

  const handleSchedule = () => {
    handleAddAppointment();
  };

  const handlePrint = () => {
    window.print();
  };

  // Handler for adding new task
  const handleAddTask = () => {
    if (leadData) {
      dispatch({
        type: 'leadTasks/openCreateTaskDrawer',
        payload: {
          task: null,
          leadId: leadData.id,
          onTaskCreated: refetch,
        },
      });
    }
  }; // Handler for adding new task
  const handleAddStandbyTask = () => {
    if (leadData) {
      dispatch({
        type: 'leadTasks/openCreateTaskDrawer',
        payload: {
          task: null,
          title: intl.formatMessage({
            id: 'pages.lead.next_action_standby_task',
            defaultMessage: 'Standby Task',
          }),
          dueDateInDays: 7,
          leadId: leadData.id,
          onTaskCreated: refetch,
        },
      });
    }
  };

  // Handler for adding new appointment
  const handleAddAppointment = () => {
    if (leadData) {
      dispatch({
        type: 'serviceRequest/toggleCreateServiceRequestDrawer',
        payload: {
          openAppointmentDetails: true,
          lead: leadData,
          then: refetch,
        },
      });
    }
  };

  const getEvents = useCallback(
    (filterInactive = false) => {
      const concatenatedEvents = [
        ...processCalendarEvents(leadData.calendarEvents || []),
        ...processServiceRequestsForCalendar(
          leadData.service_requests || [],
        ).filter((appointment) =>
          filterInactive
            ? !INACTIVE_SERVICE_REQUEST_STATUSES.includes(appointment.status)
            : true,
        ),
      ];

      return concatenatedEvents.sort((a, b) =>
        a.utc_start_time > b.utc_start_time ? -1 : 1,
      );
    },
    [leadData],
  );
  const SKELETON = useMemo(
    () => (
      <>
        {/* Left Column - Loading */}
        <div className={styles.leftColumn}>
          <Space direction="vertical" className={styles.fullWidth} size="large">
            <Skeleton active paragraph={{ rows: 4 }} />
            <Skeleton active paragraph={{ rows: 3 }} />
            <Skeleton active paragraph={{ rows: 3 }} />
            <Skeleton active paragraph={{ rows: 3 }} />
          </Space>
        </div>

        {/* Right Column - Loading */}
        <div className={styles.rightColumn}>
          <Space direction="vertical" className={styles.fullWidth} size="large">
            <Skeleton active paragraph={{ rows: 2 }} />
            <Skeleton active paragraph={{ rows: 2 }} />
            <Skeleton active paragraph={{ rows: 2 }} />
            <Skeleton active paragraph={{ rows: 2 }} />
          </Space>
        </div>
      </>
    ),
    [],
  );

  const renderSkeleton = (loading && isEmpty(leadData)) || isEmpty(leadData);

  const activeEvents = useMemo(() => getEvents(true), [getEvents]);
  const allEvents = useMemo(() => getEvents(false), [getEvents]);

  const mergedFiles = useMemo(() => getMergedLeadFiles(leadData), [leadData]);

  const displayedAppointments = useMemo(() => {
    const sliced = activeEvents.slice(0, 2);
    if (
      highlightedAppointmentId &&
      !sliced.find((a) => a.id === highlightedAppointmentId)
    ) {
      const highlighted = allEvents.find(
        (a) => a.id === highlightedAppointmentId,
      );
      if (highlighted) {
        sliced.push(highlighted);
      }
    }
    return sliced;
  }, [activeEvents, allEvents, highlightedAppointmentId]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentLayout}>
        {renderSkeleton ? (
          SKELETON
        ) : (
          <>
            <div className={styles.leftColumn}>
              <div className={styles.leftInner}>
                <CustomerHeaderCard
                  customer={leadData.customer}
                  lead={leadData}
                  onEmail={handleEmail}
                  onCall={handleCall}
                  onSchedule={handleSchedule}
                  onPrint={handlePrint}
                />

                <NextActionCard
                  leadData={leadData}
                  onRefresh={refetch}
                  onAddTask={handleAddTask}
                  onAddStandbyTask={handleAddStandbyTask}
                  onAddAppointment={handleAddAppointment}
                />

                <MetadataRow leadDetails={leadData} />

                <CustomerInfoCard
                  customerInfo={leadData.customer}
                  leadId={leadData.id}
                  lead={leadData}
                  extraFields={leadData.customer_extra_fields}
                  onRefresh={refetch}
                />

                <ProjectInfoCard
                  leadId={leadData.id}
                  projectInfo={leadData}
                  onRefresh={refetch}
                />

                <Flex gap={8}>
                  <ArchiveAction leadData={leadData} />
                  {onDelete && (
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={onDelete}
                    >
                      <F
                        id="common.actions.delete_permanently"
                        defaultMessage="Delete permanently"
                      />
                    </Button>
                  )}
                </Flex>
              </div>
            </div>

            <div className={styles.rightColumn}>
              <Space
                direction="vertical"
                className={styles.fullWidth}
                size={16}
              >
                <AppointmentsCard
                  appointments={displayedAppointments}
                  highlightedAppointmentId={highlightedAppointmentId}
                  count={allEvents.length}
                  leadData={leadData}
                  onAdd={handleAddAppointment}
                  onRefresh={refetch}
                  onShowAll={() => openDrawer('appointments')}
                />
                <TasksCard
                  tasks={leadData.tasks || []}
                  count={leadData.tasks_count}
                  leadId={projectId}
                  onAdd={handleAddTask}
                  onShowAll={() => openDrawer('tasks')}
                  onRefresh={refetch}
                />

                <ActivitiesCard
                  activities={activities.slice(0, 2)}
                  count={activities.length || leadData.activities_count}
                  onShowAll={() => openDrawer('activities')}
                />

                <MessagesCard
                  messages={messages.slice(0, 2)}
                  count={messages.length}
                  onShowAll={(message) =>
                    openDrawer('messages', { selectedMessage: message })
                  }
                  onRefresh={refetch}
                />

                <MailCard
                  refreshToken={mailRefreshToken}
                  leadId={projectId}
                  onShowAll={(thread) => openDrawer('mail', { thread })}
                  onCompose={() => openDrawer('mailCompose')}
                />

                <CommentsCard
                  comments={comments.slice(0, 2)}
                  count={parseInt(leadData.comments_count || '0', 10)}
                  onAdd={() => openDrawer('comments', { mode: 'create' })}
                  onShowAll={() => openDrawer('comments')}
                />

                <FilesCard
                  files={mergedFiles.slice(0, 2)}
                  count={mergedFiles.length}
                  onAdd={() => openDrawer('files', { mode: 'create' })}
                  onShowAll={() => openDrawer('files')}
                />

                {hasSalesInfluencersAccess &&
                  (leadData.sales_influencers?.length ?? 0) > 0 && (
                    <SalesInfluencersCard
                      salesInfluencers={leadData.sales_influencers}
                      trickparkSessions={leadData.trickpark_sessions}
                    />
                  )}
                {hasSalesInfluencersAccess && (leadData.sales_influencers?.length ?? 0) > 0 && (
                  <SalesInfluencersCard
                    salesInfluencers={leadData.sales_influencers}
                    trickparkSessions={leadData.trickpark_sessions}
                  />
                )}

                {hasCaratAccess && (
                  <CaratPlanningsCard
                    leadId={projectId}
                    onSelect={(planning, plannings) =>
                      openDrawer('caratPlannings', { plannings, selectedPlanning: planning })
                    }
                    onShowAll={(plannings) => openDrawer('caratPlannings', { plannings })}
                  />
                )}
              </Space>
            </div>
          </>
        )}
      </div>
      {!renderSkeleton && (
        <Flex className={styles.footerLayout}>
          <LeadDetailsDrawerProperties
            propertyProps={{
              [LEAD_PROPERTIES.MARKETING_CHANNEL]: {
                isTable: false,
              },
              [LEAD_PROPERTIES.MARKETING_SOURCE]: {
                isTable: false,
              },
              [LEAD_PROPERTIES.MARKETING_CAMPAIGN]: {
                isTable: false,
              },
            }}
            grid={null}
            properties={LEAD_DETAILS_FOOTER_PROPERTIES}
            lead={leadData}
          />
        </Flex>
      )}

      <DrawerLayout
        open={activeDrawerType === 'caratPlannings'}
        title={intl.formatMessage({
          id: 'entity.kitchen_planning.singular',
          defaultMessage: 'Kitchen planning',
        })}
        onClose={closeDrawer}
      >
        {activeDrawerType === 'caratPlannings' && (
          <CaratPlanningsDrawer
            plannings={activeDrawerPayload?.plannings || []}
            selectedPlanning={activeDrawerPayload?.selectedPlanning}
            leadDetails={leadData}
          />
        )}
      </DrawerLayout>

      <DrawerLayout
        open={activeDrawerType === 'activities'}
        title={intl.formatMessage({
          id: 'entity.activity.plural',
          defaultMessage: 'Activities',
        })}
        onClose={closeDrawer}
      >
        {activeDrawerType === 'activities' && leadData && (
          <ActivitiesDrawer leadId={projectId} leadDetails={leadData} />
        )}
      </DrawerLayout>

      <DrawerLayout
        open={activeDrawerType === 'tasks'}
        title={intl.formatMessage({
          id: 'entity.task.plural',
          defaultMessage: 'Tasks',
        })}
        onClose={closeDrawer}
      >
        {leadData && (
          <TasksDrawer
            leadId={projectId}
            leadDetails={leadData}
            onRefresh={refetch}
            payload={activeDrawerPayload}
          />
        )}
      </DrawerLayout>

      <DrawerLayout
        open={activeDrawerType === 'appointments'}
        title={intl.formatMessage({
          id: 'entity.appointment.plural',
          defaultMessage: 'Appointments',
        })}
        onClose={closeDrawer}
      >
        {leadData && (
          <AppointmentsDrawer
            leadId={projectId}
            leadDetails={leadData}
            onRefresh={refetch}
            payload={activeDrawerPayload}
          />
        )}
      </DrawerLayout>

      <DrawerLayout
        open={activeDrawerType === 'comments'}
        title={intl.formatMessage({
          id: 'entity.comment.plural',
          defaultMessage: 'Comments',
        })}
        onClose={closeDrawer}
      >
        {leadData && (
          <CommentsDrawer
            leadId={projectId}
            leadDetails={leadData}
            onRefresh={refetch}
            payload={activeDrawerPayload}
          />
        )}
      </DrawerLayout>

      <DrawerLayout
        open={activeDrawerType === 'files'}
        title={intl.formatMessage({
          id: 'entity.file.plural',
          defaultMessage: 'Files',
        })}
        onClose={closeDrawer}
      >
        {leadData && (
          <FilesDrawer
            leadId={projectId}
            leadDetails={leadData}
            onRefresh={refetch}
            payload={activeDrawerPayload}
          />
        )}
      </DrawerLayout>

      <DrawerLayout
        open={activeDrawerType === 'messages'}
        title={`${intl.formatMessage({
          id: 'entity.message.plural',
          defaultMessage: 'Messages',
        })} - ${leadData?.title || ''}${
          getCustomerLabel(leadData?.customer) ? ` (${getCustomerLabel(leadData?.customer)})` : ''
        }`}
        onClose={closeDrawer}
        width={1200}
      >
        {leadData && (
          <MessagesDrawer
            projectId={projectId}
            project={leadData}
            payload={activeDrawerPayload}
          />
        )}
      </DrawerLayout>

      <DrawerLayout
        open={activeDrawerType === 'mail'}
        // No dangling " - " when the lead has no title.
        title={[
          intl.formatMessage({
            id: 'email.card.title',
            defaultMessage: 'Emails (Outlook)',
          }),
          leadData?.title,
        ]
          .filter(Boolean)
          .join(' — ')}
        onClose={closeDrawer}
        width={1200}
      >
        {leadData && (
          <EmailApp
            leadId={projectId}
            customerEmail={leadData?.customer?.email}
            initialThread={activeDrawerPayload?.thread}
          />
        )}
      </DrawerLayout>

      {/* Compose without the thread list behind it — the composer is already a
          drawer of its own, so the + needs no second surface. */}
      {leadData && (
        <EmailComposer
          open={activeDrawerType === 'mailCompose'}
          leadId={projectId}
          defaultTo={reachable?.email || leadData?.customer?.email}
          onClose={closeDrawer}
          onSent={() => setMailRefreshToken((token) => token + 1)}
        />
      )}
    </div>
  );
};

export default ProjectDetails;
